/**
 * Holds one Beacon socket while either reason to have one holds — the panel is
 * open, or a conversation is live — rather than one per pageview. Gestures
 * heading into chat warm it earlier still: beacon holds the guide's first tool
 * call for ~13s, so a handshake started at the first send spends that window
 * connecting.
 *
 * Across tabs only the holder of the tab lock keeps a socket: a send or an open
 * panel takes the lock from whichever tab has it, and anything else queues.
 *
 * Not in `bootstrap.ts`: the triggers are actions, and nothing under `actions/`
 * or `lib/` may import the bootstrap entry point — that would invert the
 * dependency and pull the bootstrap graph into the widget chunk.
 */

import type { BeaconGateway } from '@/gateways/BeaconGateway';
import { registerBeaconPinger } from '@/lib/beacon/beaconHeartbeat';
import type { BeaconTabLock, BeaconTabLockHooks } from '@/lib/beacon/beaconTabLock';
import { logger } from '@/lib/logger';

type BeaconGatewayFactory = (canConnect: () => boolean) => Promise<BeaconGateway>;

type BeaconTabLockFactory = (hooks: BeaconTabLockHooks) => Promise<BeaconTabLock>;

interface BeaconTabLockOptions {
  appId: string;
  create: BeaconTabLockFactory;
}

let createGateway: BeaconGatewayFactory | null = null;
let createTabLock: BeaconTabLockFactory | null = null;
let tabLock: BeaconTabLock | null = null;
let tabLockAppId: string | null = null;
let tabLockLoading = false;
// Bumped when the app changes; a lock still loading for the old app is disposed on arrival.
let tabLockRun = 0;
let pendingTake = false;
let gateway: BeaconGateway | null = null;
let loading = false;
// Bumped when a load in flight is invalidated; its gateway is destroyed on arrival.
let generation = 0;
let widgetOpen = false;
let conversationLive = false;
let conversationTerminated = false;
let warm = false;

export function registerBeaconGatewayFactory(factory: BeaconGatewayFactory | null): void {
  createGateway = factory;
  abandonLoad();
}

export function configureBeaconTabLock(options: BeaconTabLockOptions): void {
  createTabLock = options.create;
  const appId = options.appId;
  // One app's lock survives a re-init, so the tab the shopper last chatted in reclaims it before another connects.
  if (appId === tabLockAppId) {
    return;
  }

  tabLock?.dispose();
  tabLock = null;
  tabLockAppId = appId;
  tabLockLoading = false;
  tabLockRun += 1;
  pendingTake = false;
}

function loadTabLock(gesture: boolean): void {
  pendingTake ||= gesture;
  if (tabLockLoading || !createTabLock) {
    return;
  }

  const factory = createTabLock;
  const run = tabLockRun;
  tabLockLoading = true;
  new Promise<BeaconTabLock>((resolve) =>
    resolve(factory({ onGranted: handleGranted, onLost: dropSocket, onYield: handOff })),
  ).then(
    (lock) => {
      if (run !== tabLockRun) {
        lock.dispose();
        return;
      }

      tabLockLoading = false;
      tabLock = lock;
      const take = pendingTake;
      pendingTake = false;
      seekSocket(take);
    },
    (err: unknown) => {
      if (run !== tabLockRun) {
        return;
      }

      tabLockLoading = false;
      pendingTake = false;
      // The lock ships in the gateway's chunk.
      logger.warn('[beacon] Failed to load the gateway', err);
    },
  );
}

function buildGateway(): void {
  if (gateway || loading || !createGateway || conversationTerminated) {
    return;
  }

  const factory = createGateway;
  const run = generation;
  loading = true;
  new Promise<BeaconGateway>((resolve) => resolve(factory(canConnect))).then(
    (built) => {
      if (run !== generation) {
        built.destroy();
        return;
      }

      loading = false;
      gateway = built;
      gateway.connect();
      // Pings only if a session is live; a first send usually starts them via chatSessionStarted.
      registerBeaconPinger(gateway);
    },
    (err: unknown) => {
      if (run !== generation) {
        return;
      }

      loading = false;
      logger.warn('[beacon] Failed to load the gateway', err);
    },
  );
}

function abandonLoad(): void {
  generation += 1;
  loading = false;
}

export function ensureBeaconGateway(): void {
  conversationTerminated = false;
  conversationLive = true;
  seekSocket(true);
}

/**
 * A conversation turned out to be live without this tab doing anything — found
 * at hydration, or heard in an inbound message — so the socket may stay in
 * whichever tab the shopper is chatting from.
 */
export function beaconConversationObserved(): void {
  conversationTerminated = false;
  conversationLive = true;
  seekSocket(false);
}

/**
 * Start the handshake for a gesture heading into chat, claiming nothing.
 * Whatever it reaches — a revealed panel, a send — claims the socket there.
 */
export function warmBeaconConnection(): void {
  warm = true;
  seekSocket(false);
}

/**
 * Give up a warmed connection whose gesture reached neither claim — a prompt
 * whose launcher is gone, a dismissed Chat Bar. No later close would drop it.
 */
export function coolBeaconConnection(): void {
  if (widgetOpen || conversationLive) {
    return;
  }

  releaseSocket();
}

/**
 * The panel is on screen: connect, and hold the socket until it closes.
 */
export function beaconWidgetOpened(): void {
  widgetOpen = true;
  seekSocket(true);
}

/**
 * The panel is gone: drop the socket unless a conversation claimed it —
 * minimized is not over.
 */
export function beaconWidgetClosed(): void {
  widgetOpen = false;
  if (conversationLive) {
    return;
  }

  releaseSocket();
}

/**
 * Drop the socket when a conversation is genuinely over: a connection held for
 * a conversation nobody is having is the pageview-scaled cost this module
 * exists to remove, and credentials outlive conversations, so a returning
 * visitor would otherwise hold one on every page. Only for a terminal end — a
 * network drop leaves the conversation live and chat-rocket reconnecting.
 *
 * No rebuild until the next conversation, even for a panel still on screen: the
 * heartbeat only runs during a session, so an idle socket would stop refreshing
 * the service's connection TTL and go stale while still reading healthy.
 */
export function beaconConversationEnded(): void {
  conversationTerminated = true;
  conversationLive = false;
  releaseSocket();
}

/**
 * Drops the socket and both claims on it; the pipeline stop owns this. A
 * conversation that terminated stays terminated — a re-init is not a new
 * conversation, and the panel the re-arm finds is the same idle one.
 */
export function destroyBeaconGateway(): void {
  releaseSocket();
  widgetOpen = false;
  conversationLive = false;
}

/**
 * Restore the claims a pipeline stop cleared. Neither is a gesture, so this
 * tab takes the socket from another only if the shopper last chatted here.
 */
export function rearmBeaconConnection(hasActiveConversation: boolean, panelOnScreen: boolean): void {
  if (panelOnScreen) {
    widgetOpen = true;
  }
  if (hasActiveConversation) {
    conversationTerminated = false;
    conversationLive = true;
  }
  seekSocket(false);
}

function seekSocket(gesture: boolean): void {
  if (!shouldHold()) {
    return;
  }

  // Only tests register a gateway without a tab lock; that tab connects on its own.
  if (!createTabLock) {
    buildGateway();
    return;
  }

  if (!tabLock) {
    loadTabLock(gesture);
    return;
  }

  if (gesture) {
    tabLock.take();
  } else {
    tabLock.queue();
  }
  // Also rebuilds after a load that failed while this tab kept the lock.
  if (tabLock.isHeld()) {
    buildGateway();
  }
}

function shouldHold(): boolean {
  return createGateway !== null && !conversationTerminated && (widgetOpen || conversationLive || warm);
}

function handleGranted(): void {
  if (!shouldHold()) {
    tabLock?.release();
    return;
  }

  buildGateway();
}

// A tool call still running after DRAIN_TIMEOUT_MS is answered in the next tab by toolCallRelay, not run again.
async function handOff(): Promise<void> {
  const run = generation;
  await gateway?.drain(DRAIN_TIMEOUT_MS);
  if (run === generation) {
    dropSocket();
  }
}

function canConnect(): boolean {
  return tabLock ? tabLock.isHeld() : !createTabLock;
}

function releaseSocket(): void {
  dropSocket();
  pendingTake = false;
  tabLock?.release();
}

function dropSocket(): void {
  abandonLoad();
  registerBeaconPinger(null);
  gateway?.destroy();
  gateway = null;
  warm = false;
}

// Shorter than the tab lock's steal timeout, so a prompt hand-off finishes before any steal.
const DRAIN_TIMEOUT_MS = 5000;
